import { useState } from "react";
import {
  Cloud, Lightbulb, Moon, HelpCircle, Pen,
  Leaf, Clock, User, ChevronRight,
} from "lucide-react";
import { tokens } from "./styles/designTokens";

const F  = tokens.fontFamily.base;
const SP = tokens.spacing;
const R  = tokens.radius;
const FS = tokens.fontSize;
const FW = tokens.fontWeight;
const LH = tokens.lineHeight;

// ── 感情タグ定義 ───────────────────────────────────────────────
// 「うまく言葉にできない」は必須。PathNoteの入口として最も大切なタグ。
const EMOTION_TAGS = [
  { label: "モヤモヤ",             Icon: Cloud,       iconColor: "#94A3B8" },
  { label: "疲れた",               Icon: Moon,        iconColor: "#7B8FAE" },
  { label: "気づき",               Icon: Lightbulb,   iconColor: "#D9A56B" },
  { label: "迷い",                 Icon: HelpCircle,  iconColor: "#B5A7C7" },
  { label: "うまく言葉にできない", Icon: Pen,         iconColor: "#C97B7B", highlight: true },
];

// ── 過去記録ナビ ───────────────────────────────────────────────
const NAV_ITEMS = [
  { tab: "stock", Icon: Leaf,  label: "自分の言葉", desc: "積み重なってきた言葉を見る" },
  { tab: "log",   Icon: Clock, label: "自分ログ",   desc: "時系列で気持ちを振り返る"  },
  { tab: "page",  Icon: User,  label: "マイページ", desc: "記録のまとめを確認する"    },
];

// ── 感情タグボタン ────────────────────────────────────────────
function EmotionTag({ label, Icon, iconColor, highlight, onClick }) {
  const [hovered, setHovered] = useState(false);

  const base = {
    display: "inline-flex", alignItems: "center", gap: SP.base,
    padding: `${SP.lg}px ${SP["3xl"]}px`,
    border: `1.5px solid ${hovered ? tokens.neutral.borderStrong : tokens.neutral.border}`,
    borderRadius: R.pill,
    fontSize: FS.md, fontWeight: FW.medium,
    color: tokens.text.primary, fontFamily: F, cursor: "pointer",
    transition: "background 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease",
    boxShadow: hovered ? tokens.shadow.sm : "none",
  };

  // 「うまく言葉にできない」だけ背景をわずかに温かく
  const bg = highlight
    ? hovered ? "#F5E8E6" : "#FDF0EF"
    : hovered ? tokens.neutral.surfaceAlt : tokens.neutral.surface;

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ ...base, background: bg }}
    >
      <Icon size={14} strokeWidth={1.6} color={iconColor}/>
      {label}
    </button>
  );
}

// ── 過去記録カード ────────────────────────────────────────────
function NavCard({ Icon, label, desc, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center",
        gap: SP["4xl"],
        padding: `${SP["3xl"]}px ${SP["4xl"]}px`,
        background: hovered ? tokens.neutral.surfaceAlt : tokens.neutral.surface,
        border: `1px solid ${tokens.neutral.border}`,
        borderRadius: R.xl,
        cursor: "pointer", fontFamily: F, textAlign: "left",
        width: "100%", boxShadow: tokens.shadow.sm,
        transition: "background 0.16s ease",
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: "50%",
        background: tokens.accent.light,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <Icon size={18} strokeWidth={1.5} color={tokens.accent.primary}/>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: FS.md, fontWeight: FW.semibold, color: tokens.text.primary, marginBottom: SP.xxs }}>
          {label}
        </div>
        <div style={{ fontSize: FS.sm, color: tokens.text.tertiary, lineHeight: LH.snug }}>
          {desc}
        </div>
      </div>
      <ChevronRight size={16} strokeWidth={1.5} color={tokens.text.tertiary} style={{ flexShrink: 0 }}/>
    </button>
  );
}

// ── LandingPage ──────────────────────────────────────────────
// onStart(tab?) : tab省略時は "note"（STEP1）、"stock"/"log"/"page" も受け取れる
export function LandingPage({ onStart }) {
  const inner = {
    maxWidth: tokens.layout.contentMaxWidth,
    margin: "0 auto",
    padding: `0 ${SP["4xl"]}px`,
    boxSizing: "border-box",
    width: "100%",
  };

  return (
    <div style={{ flex: 1, fontFamily: F, background: tokens.neutral.bg }}>

      {/* ── 感情タグ入口 ────────────────────────────────── */}
      <div style={{ ...inner, paddingTop: 72, paddingBottom: 56 }}>

        {/* グリーティング */}
        <p style={{
          fontSize: FS.sm, color: tokens.text.tertiary,
          marginBottom: SP["5xl"],
          letterSpacing: tokens.letterSpacing.normal,
        }}>
          今日も、ここに来てくれてありがとう。
        </p>

        {/* リード */}
        <h1 style={{
          fontSize: "clamp(20px, 5.5vw, 26px)",
          fontWeight: FW.semibold,
          color: tokens.text.primary,
          lineHeight: LH.snug,
          letterSpacing: tokens.letterSpacing.tight,
          margin: `0 0 ${SP["7xl"]}px`,
        }}>
          今の気持ちに<br/>近いものを選んでください。
        </h1>

        {/* 感情タグ */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: SP.lg }}>
          {EMOTION_TAGS.map((tag) => (
            <EmotionTag
              key={tag.label}
              {...tag}
              onClick={() => onStart("note")}
            />
          ))}
        </div>
      </div>

      {/* ── 区切り ─────────────────────────────────────── */}
      <div style={{ borderTop: `1px solid ${tokens.neutral.border}` }}/>

      {/* ── 過去の記録ナビ ───────────────────────────── */}
      <div style={{ ...inner, paddingTop: SP["7xl"], paddingBottom: SP["9xl"] }}>
        <p style={{
          fontSize: FS.xxs, color: tokens.text.tertiary,
          fontWeight: FW.semibold, letterSpacing: tokens.letterSpacing.widest,
          marginBottom: SP["5xl"],
        }}>
          過去の記録
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: SP.lg }}>
          {NAV_ITEMS.map(({ tab, Icon, label, desc }) => (
            <NavCard
              key={tab}
              Icon={Icon}
              label={label}
              desc={desc}
              onClick={() => onStart(tab)}
            />
          ))}
        </div>
      </div>

    </div>
  );
}
